import {
  CF_BETA_BLOCK_FEATURE,
  canEnterCfBeta,
  isPrerenderStaffEmail,
} from '@prerender/prerender-shared/FE';
import { FeatureFlagValue } from '../../hooks/FeatureFlag';
import {
  usePrerenderEmail,
  usePrerenderUserFeatures,
} from '../../hooks/usePrerenderUser';

/**
 * The kill switch, re-exported rather than re-declared.
 *
 * It used to be a literal here, alongside a second copy of the admission rule.
 * The two copies agreed, and nothing kept them agreeing — so the name and the
 * rule now both come from `@prerender/prerender-shared`, which is also what
 * rest-api's `CfBetaEntryGuard` reads.
 */
export const CLOUDFLARE_BETA_BLOCK = CF_BETA_BLOCK_FEATURE;

/** How an account got in. Reported so the arms can be read apart. */
export type CfBetaEntrySource = 'rollout' | 'internal';

type CfBetaAccessUser = {
  email: string | null | undefined;
  featureFlags: FeatureFlagValue[];
};

/**
 * Who sees the automated Cloudflare beta (PRE-3294 / R1).
 *
 * The admission decision is entirely `canEnterCfBeta`'s — block first, then
 * staff, then the rolled-out feature. This function only *labels* how an
 * account that may enter got in, because the two arms have to be readable apart
 * in the reports. It deliberately holds no rule of its own: a gate the dashboard
 * enforces differently from rest-api is not a gate.
 */
export function cfBetaEntrySource({
  email,
  featureFlags,
}: CfBetaAccessUser): CfBetaEntrySource | null {
  if (!canEnterCfBeta({ email, features: featureFlags })) return null;

  return isPrerenderStaffEmail(email) ? 'internal' : 'rollout';
}

export function hasCloudflareBetaAccess(user: CfBetaAccessUser): boolean {
  return cfBetaEntrySource(user) !== null;
}

export function useCfBetaEntrySource(): CfBetaEntrySource | null {
  const email = usePrerenderEmail();
  const featureFlags = usePrerenderUserFeatures();

  return cfBetaEntrySource({ email, featureFlags });
}
